import { FC, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { getCanvasAnimationLayerSx } from "./canvasAnimationLayerSx";
import type { CanvasAnimationLayoutVariant } from "./canvasBackgroundTheme";
import { PRIDE_STRIPES_COUNT, prideStripeColor } from "./prideStripes";
import { resizeCanvasKeepingPositions } from "./resizeCanvasKeepingPositions";
import { resolveCanvasSurfaceSize } from "./resolveCanvasSurfaceSize";
import { subscribeVisibilityAwareAnimationLoop } from "./subscribeVisibilityAwareAnimationLoop";

const MOBILE_CONFETTI_RATIO = 0.5;
const CONFETTI_COUNT = 55;
const BUNTING_ALPHA = 0.45;
const STRANDS_COUNT = 2;
const CORD_TENSION = 95;
const CORD_DROP = 10;
const CORD_REACH = 50;

export type PrideFlagsAnimationProps = {
  variant?: CanvasAnimationLayoutVariant;
  isMobile?: boolean;
  frameStride?: number;
};

type ConfettiParticle = {
  x: number;
  y: number;
  width: number;
  height: number;
  colorIndex: number;
  speedY: number;
  swayAmplitude: number;
  swaySpeed: number;
  swayPhase: number;
  rotation: number;
  spin: number;
  flipSpeed: number;
  flipPhase: number;
  depth: number;
};

export const PrideFlagsAnimation: FC<PrideFlagsAnimationProps> = ({
  variant = "viewportFixed",
  isMobile = false,
  frameStride = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasMaybe = canvasRef.current;
    if (!canvasMaybe) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const contextMaybe = canvasMaybe.getContext("2d");
    if (!contextMaybe) {
      return;
    }

    const canvasEl: HTMLCanvasElement = canvasMaybe;
    const context: CanvasRenderingContext2D = contextMaybe;

    function resizeCanvas(): void {
      const { width, height } = resolveCanvasSurfaceSize(canvasEl);
      canvasEl.width = width;
      canvasEl.height = height;
    }

    resizeCanvas();

    function mkConfetti(spawnOnScreen: boolean): ConfettiParticle {
      const height = canvasEl.height;
      return {
        x: Math.random() * canvasEl.width,
        y: spawnOnScreen ? Math.random() * height : -20 - Math.random() * height * 0.3,
        width: (6 + Math.random() * 7) * 1.2,
        height: (3 + Math.random() * 4) * 1.2,
        colorIndex: Math.floor(Math.random() * PRIDE_STRIPES_COUNT),
        speedY: 0.5 + Math.random() * 1.1,
        swayAmplitude: 0.4 + Math.random() * 1.2,
        swaySpeed: 0.01 + Math.random() * 0.03,
        swayPhase: Math.random() * Math.PI * 2,
        rotation: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.12,
        flipSpeed: 0.04 + Math.random() * 0.07,
        flipPhase: Math.random() * Math.PI * 2,
        depth: 0.55 + Math.random() * 0.45,
      };
    }

    const confettiCount = isMobile ? Math.round(CONFETTI_COUNT * MOBILE_CONFETTI_RATIO) : CONFETTI_COUNT;
    const confetti: ConfettiParticle[] = Array.from({ length: confettiCount }, () => mkConfetti(true));

    function onResize(): void {
      resizeCanvasKeepingPositions(canvasEl, confetti);
    }

    window.addEventListener("resize", onResize);

    let frame = 0;

    function cordEnds(strand: number): [[number, number], [number, number]] {
      const width = canvasEl.width;
      const height = canvasEl.height;
      const top = -0.03 * height;
      if (strand === 0) {
        return [
          [-0.02 * width, (CORD_DROP / 100) * height],
          [(CORD_REACH / 100) * width, top],
        ];
      }
      return [
        [1.02 * width, (CORD_DROP / 100) * height],
        [((100 - CORD_REACH) / 100) * width, top],
      ];
    }

    function cordAt(progress: number, strand: number): [number, number] {
      const [[ax, ay], [bx, by]] = cordEnds(strand);
      const slack = (100 - CORD_TENSION) / 100;
      const sag = canvasEl.height * (0.02 + slack * 0.3);
      const breathe = Math.sin(frame * 0.006 + strand * 1.7) * 2.5;
      const wave = Math.sin(progress * Math.PI * 2.2 - frame * 0.012 + strand * 2.1) * 5 * Math.sin(Math.PI * progress);
      return [
        ax + (bx - ax) * progress,
        ay + (by - ay) * progress + Math.sin(Math.PI * progress) * sag + breathe + wave,
      ];
    }

    function pennantTilt(progress: number, strand: number): number {
      return Math.sin(progress * Math.PI * 2.2 - frame * 0.012 + strand * 2.1) * 0.11;
    }

    function drawBunting(): void {
      const width = canvasEl.width;
      const pennantWidth = Math.max(40, Math.min(72, width / 13)) * 0.9;
      const pennantHeight = pennantWidth * 1.15;

      context.save();
      for (let strand = 0; strand < STRANDS_COUNT; strand++) {
        const [[ax, ay], [bx, by]] = cordEnds(strand);
        const cordLength = Math.hypot(bx - ax, by - ay);
        const pennantCount = Math.max(4, Math.round(cordLength / pennantWidth));

        context.beginPath();
        for (let step = 0; step <= 60; step++) {
          const [x, y] = cordAt(step / 60, strand);
          if (step === 0) {
            context.moveTo(x, y);
          } else {
            context.lineTo(x, y);
          }
        }
        context.strokeStyle = prideStripeColor(0, BUNTING_ALPHA * 0.75);
        context.lineWidth = 2.5;
        context.lineCap = "round";
        context.stroke();

        const gap = 0.12 / pennantCount;
        for (let index = 0; index < pennantCount; index++) {
          const startProgress = index / pennantCount;
          const endProgress = (index + 1) / pennantCount - gap;
          const [x0, y0] = cordAt(startProgress, strand);
          const [x1, y1] = cordAt(endProgress, strand);
          const tilt = pennantTilt((startProgress + endProgress) / 2, strand);
          const apexX = (x0 + x1) / 2 + Math.sin(tilt) * pennantHeight;
          const apexY = (y0 + y1) / 2 + Math.cos(tilt) * pennantHeight;

          for (let stripe = 0; stripe < PRIDE_STRIPES_COUNT; stripe++) {
            const near = stripe / PRIDE_STRIPES_COUNT;
            const far = (stripe + 1) / PRIDE_STRIPES_COUNT;
            context.beginPath();
            context.moveTo(x0 + (apexX - x0) * near, y0 + (apexY - y0) * near);
            context.lineTo(x1 + (apexX - x1) * near, y1 + (apexY - y1) * near);
            context.lineTo(x1 + (apexX - x1) * far, y1 + (apexY - y1) * far);
            context.lineTo(x0 + (apexX - x0) * far, y0 + (apexY - y0) * far);
            context.closePath();
            context.fillStyle = prideStripeColor(stripe, BUNTING_ALPHA);
            context.fill();
          }
        }
      }
      context.restore();
    }

    const stopAnimationLoop = subscribeVisibilityAwareAnimationLoop({ frameStride }, (frameSteps) => {
      context.clearRect(0, 0, canvasEl.width, canvasEl.height);
      frame += frameSteps;

      for (const piece of confetti) {
        piece.y += piece.speedY * frameSteps;
        piece.x += Math.sin(frame * piece.swaySpeed + piece.swayPhase) * piece.swayAmplitude * 0.3 * frameSteps;
        piece.rotation += piece.spin * frameSteps;
        if (piece.y - piece.height > canvasEl.height) {
          Object.assign(piece, mkConfetti(false));
        }

        context.save();
        context.translate(piece.x, piece.y);
        context.rotate(piece.rotation);
        context.scale(Math.cos(frame * piece.flipSpeed + piece.flipPhase), 1);
        context.fillStyle = prideStripeColor(piece.colorIndex, Math.min(0.7, 0.45 + 0.25 * piece.depth));
        context.fillRect(-piece.width / 2, -piece.height / 2, piece.width, piece.height);
        context.restore();
      }

      drawBunting();
    });

    return () => {
      stopAnimationLoop();
      window.removeEventListener("resize", onResize);
    };
  }, [isMobile, frameStride]);

  return <Box component="canvas" ref={canvasRef} sx={getCanvasAnimationLayerSx(variant)} />;
};
